'use client';

/**
 * 提供Overlay的Context
 * 提供Toast的Context
 * 提供Confetti的Context
 */

import ConfettiComponent, { ConfettiRef } from '@/components/Confetti';
import { colors } from '@/constant/colors';
import { debounce } from '@/lib/utils';
import { CircleAlert } from 'lucide-react';
import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import supabase from '@/lib/supabase';
import useAuthStore from '@/store/useAuthStore';
import { RealtimeChannel } from '@supabase/supabase-js';
import { RawNotification } from '@/constant/schema';
import { getAssetUrl } from '@/constant/assets';

type Toast = {
  id: number;
  content: React.ReactNode;
};

type OverlayContextType = {
  addToast: (content: React.ReactNode) => void;
  fireConfetti: () => void;
};

const OverlayContext = createContext<OverlayContextType | undefined>(undefined);

export function useOverlay() {
  const ctx = useContext(OverlayContext);
  if (!ctx) throw new Error('useToast must be used within OverlayProvider');
  return ctx;
}

let toastId = 0;

export default function OverlayProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const confettiRef = useRef<ConfettiRef>(null);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const user = useAuthStore(state => state.user);
  const [hasPermission, setHasPermission] = useState<NotificationPermission>('default');

  const addToast = useCallback((content: React.ReactNode) => {
    const id = ++toastId;
    setToasts((prev) => [...prev, { id, content }]);
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }, []);

  const removeToast = (id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const fireConfetti = () => {
    // 添加防抖函数调用
    debounce(() => {
      confettiRef.current?.fire();
    }, 1000)();
  }

  // 显示浏览器通知
  const showBrowserNotification = (notification: RawNotification) => {
    // 检查浏览器是否支持通知 API
    if (!('Notification' in window)) {
      console.log('This browser does not support desktop notification');
      return;
    }

    // 检查权限
    if (Notification.permission === 'granted') {
      // 创建通知
      try {
        const browserNotification = new Notification(notification.title, {
          body: notification.content,
          icon: getAssetUrl('assets/landing-logo.png'),
          tag: notification.id,
          requireInteraction: false, // 通知会自动消失
          silent: false, // 播放声音
          badge: getAssetUrl('assets/landing-logo.png'), // 在支持的设备上显示小图标
        });

        // 点击通知时的处理
        browserNotification.onclick = (event) => {
          event.preventDefault(); // 阻止默认行为
          window.focus(); // 聚焦窗口
          browserNotification.close(); // 关闭通知
          
          // 如果当前不在 quest 页面，则导航到 quest 页面
          if (!window.location.pathname.includes('/quest')) {
            window.location.href = '/quest';
          }
        };

        // 通知显示错误处理
        browserNotification.onerror = (event) => {
          console.error('Notification error:', event);
        };

        // 自动关闭通知（10秒后）
        setTimeout(() => {
          browserNotification.close();
        }, 10000);
      } catch (error) {
        console.error('Error creating notification:', error);
      }
    }
    // 不在收到通知时自动请求权限，而是在用户开始冒险时主动引导
    // else if (Notification.permission === 'default') {
    //   Notification.requestPermission().then(permission => {
    //     setHasPermission(permission);
    //     if (permission === 'granted') {
    //       showBrowserNotification(notification);
    //     }
    //   });
    // }
  };

  // 请求通知权限
  useEffect(() => {
    if ('Notification' in window) {
      console.log('Current notification permission:', Notification.permission);
      setHasPermission(Notification.permission);
      
      // 不要在页面加载时立即请求权限，等待用户交互
      // 可以在用户第一次收到通知时请求权限
    } else {
      console.log('Notification API not supported in this browser');
    }
  }, []);

  // 设置全局通知订阅
  useEffect(() => {
    if (!user) return;

    console.log('Setting up global notification subscription for user:', user.id);

    // 设置实时订阅
    const channel = supabase.channel(`global-notification:${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notification',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('New notification received globally:', payload);
          const newNotification = payload.new as RawNotification;
          
          // 显示 Toast 提示
          console.log('Showing toast for notification:', newNotification.title);
          addToast(
            <div className="flex flex-col gap-1">
              <span className="text-sm font-medium text-gray-800">{newNotification.title}</span>
            </div>
          );
          
          // 显示浏览器通知
          console.log('Attempting to show browser notification...');
          showBrowserNotification(newNotification);
        }
      )
      .subscribe((status) => {
        console.log('Global subscription status:', status);
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to global notification channel');
        }
      });

    channelRef.current = channel;

    // 清理函数
    return () => {
      if (channelRef.current) {
        console.log('Removing global notification channel subscription');
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [user, addToast]);

  return (
    <OverlayContext.Provider value={{ addToast, fireConfetti }}>
      {children}
      {/* Overlay Container */}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 items-end">
        {toasts.map((Overlay) => (
          <div
            key={Overlay.id}
            className="bg-white shadow-lg rounded px-4 py-2 mb-2 animate-slide-in relative flex flex-col"
            style={{ minWidth: 300, maxWidth: 400 }}
          >
            <div className='flex flex-row justify-between items-center'>
              <div className="flex flex-row items-center gap-2">
                <CircleAlert className='w-6 h-6' color={'white'} fill={colors.secondaryColor} />
                <span className='emphasize-text text-lg' style={{ color: colors.mainTextColor }}>New Notification</span>
              </div>
              <button
                className="text-gray-400 hover:text-gray-700 text-lg"
                onClick={() => removeToast(Overlay.id)}
              >
                ×
              </button>
            </div>
            <div>{Overlay.content}</div>
          </div>
        ))}
      </div>
      <ConfettiComponent ref={confettiRef} />
    </OverlayContext.Provider>
  );
}
